/**
 * AuthService
 *
 * Singleton that holds the active IAuthAdapter instance.
 * All authentication calls in the application go through this service —
 * no page or component imports directly from a provider SDK.
 *
 * Initialised once in Secret.js after the provider config is available.
 * Swap the adapter to change auth providers without touching any UI code.
 *
 * Usage:
 *   import AuthService from '../services/AuthService';
 *
 *   // Sign in
 *   const user = await AuthService.signInWithEmailAndPassword(email, password);
 *
 *   // Confirm password reset (from custom action page)
 *   await AuthService.confirmPasswordReset(oobCode, newPassword);
 */

import { FirebaseAuthAdapter } from './adapters/FirebaseAuthAdapter';

class AuthService {
  constructor() {
    /** @type {import('./interfaces/IAuthAdapter').IAuthAdapter} */
    this._adapter = new FirebaseAuthAdapter();
  }

  /**
   * Register a different adapter implementation at runtime.
   * Call this from Secret.js when a non-Firebase provider is configured.
   *
   * @param {import('./interfaces/IAuthAdapter').IAuthAdapter} adapter
   */
  setAdapter(adapter) {
    this._adapter = adapter;
  }

  /**
   * Initialise the current adapter with provider config.
   * Called from Secret.js after the provider SDK is set up.
   *
   * @param {Object} config - Passed through to the adapter's initialize()
   * @returns {Promise<void>}
   */
  async initialize(config) {
    await this._adapter.initialize(config);
  }

  // ---------------------------------------------------------------------------
  // Delegate all methods to the active adapter
  // ---------------------------------------------------------------------------

  async signInWithEmailAndPassword(email, password) {
    return this._adapter.signInWithEmailAndPassword(email, password);
  }

  async signInWithProvider(providerId) {
    return this._adapter.signInWithProvider(providerId);
  }

  async sendSignInLinkToEmail(email, actionCodeSettings) {
    return this._adapter.sendSignInLinkToEmail(email, actionCodeSettings);
  }

  isSignInWithEmailLink(url) {
    return this._adapter.isSignInWithEmailLink(url);
  }

  async signInWithEmailLink(email, url) {
    return this._adapter.signInWithEmailLink(email, url);
  }

  async sendPasswordResetEmail(email) {
    return this._adapter.sendPasswordResetEmail(email);
  }

  async confirmPasswordReset(oobCode, newPassword) {
    return this._adapter.confirmPasswordReset(oobCode, newPassword);
  }

  async updateProfile(user, profile) {
    return this._adapter.updateProfile(user, profile);
  }

  async getIdToken(user, forceRefresh = false) {
    return this._adapter.getIdToken(user, forceRefresh);
  }

  onAuthStateChanged(callback) {
    return this._adapter.onAuthStateChanged(callback);
  }

  async signOut() {
    return this._adapter.signOut();
  }

  getSsoProviders() {
    return this._adapter.getSsoProviders();
  }
}

// Export as a singleton — one instance shared across the entire app
const authService = new AuthService();
export default authService;
